import React from 'react'
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer'
import type { ProjectReportData } from '@/hooks/useProjectReportData'
import { ProjectSalesChart } from './ProjectSalesChart'

// Brand palette — matches the navy referenced in analytics-email.service.ts
// and the project brief. Mint accent and neutrals borrowed from the weekly digest.
const NAVY = '#001A36'
const NAVY_TINT = '#E8EEF3'
const MINT = '#1CFFBA'
const SUCCESS = '#16a34a'
const NEUTRAL_BG = '#f3f4f6'
const NEUTRAL_BORDER = '#e5e7eb'
const TEXT_PRIMARY = '#111827'
const TEXT_SECONDARY = '#374151'
const TEXT_MUTED = '#6b7280'

const PAGE_PADDING = 18

// 842 × 474 pt ≈ A4-width landscape at 16:9 (842/474 = 1.776 ≈ 16/9).
// Single page, slide-style for sharing in email / on screen.
const PAGE_SIZE: [number, number] = [842, 474]

const styles = StyleSheet.create({
  page: {
    paddingTop: PAGE_PADDING,
    paddingBottom: PAGE_PADDING,
    paddingHorizontal: PAGE_PADDING,
    fontFamily: 'Helvetica',
    color: TEXT_PRIMARY,
    fontSize: 8,
  },

  // ── Header band ──
  headerBand: {
    backgroundColor: NAVY,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 4,
    marginBottom: 6,
    borderLeftWidth: 4,
    borderLeftColor: MINT,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerLeftCol: { flexDirection: 'column', flex: 1 },
  headerTitle: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 700,
    marginBottom: 2,
  },
  headerSubtitle: { color: '#cbd5e1', fontSize: 8 },

  // ── Headline strip (synthesizes the chart in one line) ──
  headlineStrip: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 5,
    paddingHorizontal: 10,
    marginBottom: 6,
    backgroundColor: NAVY_TINT,
    borderRadius: 3,
  },
  headlineText: {
    fontSize: 10,
    fontWeight: 700,
    color: NAVY,
  },
  statusPill: {
    paddingVertical: 3,
    paddingHorizontal: 10,
    borderRadius: 999,
    fontSize: 8,
    fontWeight: 700,
  },

  // ── Body row (3 columns) ──
  bodyRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  colLeft: { width: '24%', paddingRight: 6 },
  colCenter: { width: '48%', paddingHorizontal: 3 },
  colRight: { width: '28%', paddingLeft: 6 },

  // ── Section title (small) ──
  sectionTitleSm: {
    fontSize: 8,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 3,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },

  // ── Entity cards (stacked) ──
  entityCard: {
    paddingVertical: 3,
    paddingHorizontal: 5,
    marginBottom: 2,
    backgroundColor: NEUTRAL_BG,
    borderRadius: 3,
    borderLeftWidth: 2,
    borderLeftColor: NAVY,
  },
  entityLabel: {
    fontSize: 6,
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    marginBottom: 1,
  },
  entityValue: {
    fontSize: 8,
    fontWeight: 700,
    color: TEXT_PRIMARY,
  },

  // ── Amenities row ──
  amenityRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  amenityChip: {
    flex: 1,
    paddingVertical: 3,
    paddingHorizontal: 3,
    marginRight: 3,
    backgroundColor: NEUTRAL_BG,
    borderRadius: 3,
    borderLeftWidth: 2,
    borderLeftColor: NEUTRAL_BORDER,
    alignItems: 'center',
  },
  amenityChipActive: {
    backgroundColor: '#dcfce7',
    borderLeftColor: SUCCESS,
  },
  amenityChipLast: { marginRight: 0 },
  amenityChipLabel: {
    fontSize: 7,
    fontWeight: 700,
    color: TEXT_PRIMARY,
  },
  amenityChipLabelActive: { color: SUCCESS },
  amenityChipStatus: { fontSize: 6, color: TEXT_MUTED },
  amenityChipStatusActive: { color: SUCCESS },

  // ── Mini map + address ──
  mapImage: {
    width: '100%',
    height: 70,
    objectFit: 'cover',
    borderRadius: 3,
  },
  mapPlaceholder: {
    width: '100%',
    height: 70,
    backgroundColor: NEUTRAL_BG,
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapPlaceholderText: { color: TEXT_MUTED, fontSize: 7 },
  addressLine: {
    fontSize: 8,
    color: TEXT_PRIMARY,
    marginTop: 3,
  },
  addressMuted: {
    fontSize: 7,
    color: TEXT_MUTED,
  },

  // ── KPI grid (3×3 compact) ──
  kpiGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  kpiCard: {
    width: '33.333%',
    padding: 2,
  },
  kpiBox: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: NEUTRAL_BORDER,
    borderRadius: 3,
    padding: 4,
  },
  kpiLabel: {
    fontSize: 6,
    color: TEXT_MUTED,
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  kpiValue: {
    fontSize: 9,
    fontWeight: 700,
    color: NAVY,
    marginBottom: 1,
  },
  kpiSecondary: {
    fontSize: 6,
    color: TEXT_MUTED,
  },

  // ── Typology table (full width, 7 cols) ──
  table: {
    borderTopWidth: 1,
    borderTopColor: NEUTRAL_BORDER,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: NEUTRAL_BORDER,
    paddingVertical: 2,
    paddingHorizontal: 2,
  },
  tableHeaderRow: { backgroundColor: NAVY_TINT },
  tableTotalRow: {
    borderTopWidth: 1,
    borderTopColor: NAVY,
    backgroundColor: '#ffffff',
  },
  cell: {
    fontSize: 7,
    color: TEXT_SECONDARY,
    paddingHorizontal: 2,
  },
  cellHeader: {
    fontSize: 7,
    fontWeight: 700,
    color: NAVY,
    paddingHorizontal: 2,
    textTransform: 'uppercase',
  },
  cellBold: {
    fontSize: 7,
    fontWeight: 700,
    color: TEXT_PRIMARY,
    paddingHorizontal: 2,
  },

  // ── Footer (single page → not fixed) ──
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 6,
    color: TEXT_MUTED,
    borderTopWidth: 0.5,
    borderTopColor: NEUTRAL_BORDER,
    paddingTop: 4,
    marginTop: 4,
  },
})

// 7-column typology table:
// Typology | Nr | Sold | Resv | Price Range | Avg €/m² | Area Range
const COL_FLEX: number[] = [1.6, 0.7, 0.8, 0.9, 2.2, 1.2, 2.0]
const COL_HEADERS = [
  'Typology',
  'Nr',
  'Sold',
  'Resv.',
  'Price Range',
  'Avg €/m²',
  'Area Range',
]

// ── Formatters ──
const fmtPrice = (n: number | null | undefined): string => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  const v = Number(n)
  if (v === 0) return '—'
  return `${new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 }).format(v)} €`
}
const fmtArea = (n: number | null | undefined): string => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  const v = Number(n)
  if (v === 0) return '—'
  return `${new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 }).format(v)} m²`
}
const fmtSqmPrice = (n: number | null | undefined): string => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  const v = Number(n)
  if (v === 0) return '—'
  return `${new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 }).format(v)} €`
}
const fmtPlainPct = (n: number | null | undefined, decimals = 0): string => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return `${Number(n).toFixed(decimals)}%`
}
const fmtInt = (n: number | null | undefined): string => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return Number(n).toString()
}
const fmtDecimal = (n: number | null | undefined, decimals: number): string => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—'
  return new Intl.NumberFormat('pt-PT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(n))
}
const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return '—'
  }
}
// Renders a min–max range cleanly. Returns '—' when both sides are missing.
const fmtRange = (
  min: number | null | undefined,
  max: number | null | undefined,
  fmt: (n: number | null | undefined) => string,
): string => {
  const hasMin = min !== null && min !== undefined && Number(min) > 0
  const hasMax = max !== null && max !== undefined && Number(max) > 0
  if (hasMin && hasMax) return `${fmt(min)} - ${fmt(max)}`
  if (hasMax) return `Up to ${fmt(max)}`
  if (hasMin) return `From ${fmt(min)}`
  return '—'
}

function pickStat(
  stats: Record<string, unknown> | null | undefined,
  candidates: string[],
): number | null {
  if (!stats) return null
  for (const k of candidates) {
    const v = stats[k]
    if (v === null || v === undefined) continue
    const n = Number(v)
    if (!Number.isNaN(n)) return n
  }
  return null
}

interface Props {
  data: ProjectReportData
}

export function ProjectReportDocument({ data }: Props): JSX.Element {
  const { project, entities, statistics, typology_breakdown, sales_progression, static_map_url, generated_at } = data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stats: Record<string, unknown> = (statistics ?? {}) as any

  // Map URL is pre-built server-side (frontend has no Vite env file).
  const mapUrl = static_map_url || null

  const totalUnits = pickStat(stats, ['totalUnits', 'total_units'])
  const availableUnits = pickStat(stats, ['availableUnits', 'available_units'])
  const soldUnits = pickStat(stats, ['soldUnits', 'sold_units'])
  const reservedUnits = pickStat(stats, ['reservedUnits', 'reserved_units'])
  const conversionRate = pickStat(stats, ['conversionRate', 'conversion_rate'])
  const avgPrice = pickStat(stats, ['avgPrice', 'avg_price'])
  const minPrice = pickStat(stats, ['minPrice', 'min_price'])
  const maxPrice = pickStat(stats, ['maxPrice', 'max_price'])
  const avgSqmPrice = pickStat(stats, ['avgSqmPrice', 'avg_sqm_price'])
  const minSqmPrice = pickStat(stats, ['minSqmPrice', 'min_sqm_price'])
  const maxSqmPrice = pickStat(stats, ['maxSqmPrice', 'max_sqm_price'])
  const avgGpa = pickStat(stats, ['avgGpa', 'avg_gpa'])
  const minGpa = pickStat(stats, ['minGpa', 'min_gpa'])
  const maxGpa = pickStat(stats, ['maxGpa', 'max_gpa'])
  const avgMonthsToSell = pickStat(stats, ['avgMonthsToSell', 'avg_months_to_sell'])
  const avgDaysToSell = pickStat(stats, ['avgDaysToSell', 'avg_days_to_sell'])
  const timeSinceListingMonths = pickStat(stats, ['timeSinceListingMonths', 'time_since_listing_months'])
  const unitsWithParking = pickStat(stats, ['unitsWithParking', 'units_with_parking'])
  const parkingPercentage = pickStat(stats, ['parkingPercentage', 'parking_percentage'])

  // Backend returns `{ data: rows, total: row|null }` envelope.
  const rows = (typology_breakdown?.data ?? []).filter((r) => r != null)
  const total = typology_breakdown?.total ?? null

  const statusBadge = computeStatusBadge(project.status, project.sold_out_date)
  const headlineText = buildHeadline(soldUnits, reservedUnits, totalUnits)

  return (
    <Document title={`${project.name} — Project Report`} author="Blue Tagus">
      <Page size={PAGE_SIZE} style={styles.page}>
        {/* ── Header ────────────────────────────────────── */}
        <View style={styles.headerBand} wrap={false}>
          <View style={styles.headerLeftCol}>
            <Text style={styles.headerTitle}>{project.name || 'Unnamed Project'}</Text>
            <Text style={styles.headerSubtitle}>
              Project Report — Generated {fmtDate(generated_at)}
            </Text>
          </View>
        </View>

        {/* ── Headline strip ────────────────────────────── */}
        <View style={styles.headlineStrip} wrap={false}>
          <Text style={styles.headlineText}>{headlineText}</Text>
          <Text
            style={[
              styles.statusPill,
              { backgroundColor: statusBadge.background, color: statusBadge.text },
            ]}
          >
            {statusBadge.label}
          </Text>
        </View>

        {/* ── Body: 3 columns ───────────────────────────── */}
        <View style={styles.bodyRow} wrap={false}>
          {/* Left rail */}
          <View style={styles.colLeft}>
            <Text style={styles.sectionTitleSm}>Stakeholders</Text>
            <View style={styles.entityCard}>
              <Text style={styles.entityLabel}>Architect</Text>
              <Text style={styles.entityValue}>{entities?.architect?.name || '—'}</Text>
            </View>
            <View style={styles.entityCard}>
              <Text style={styles.entityLabel}>Developer</Text>
              <Text style={styles.entityValue}>{entities?.developer?.name || '—'}</Text>
            </View>
            <View style={styles.entityCard}>
              <Text style={styles.entityLabel}>Broker</Text>
              <Text style={styles.entityValue}>{entities?.broker?.name || '—'}</Text>
            </View>

            <View style={{ marginTop: 2 }}>
              <Text style={styles.sectionTitleSm}>Amenities</Text>
              <View style={styles.amenityRow}>
                <AmenityChip label="Parking" active={!!project.has_parking} />
                <AmenityChip label="Pool" active={!!project.has_pool} />
                <AmenityChip label="Gym" active={!!project.has_gym} isLast />
              </View>
            </View>

            <View style={{ marginTop: 2 }}>
              <Text style={styles.sectionTitleSm}>Location</Text>
              {mapUrl ? (
                <Image src={mapUrl} style={styles.mapImage} />
              ) : (
                <View style={styles.mapPlaceholder}>
                  <Text style={styles.mapPlaceholderText}>No coordinates</Text>
                </View>
              )}
              <Text style={styles.addressLine}>{project.address || '—'}</Text>
              <Text style={styles.addressMuted}>
                {[project.city, project.district].filter(Boolean).join(' · ') || '—'}
              </Text>
            </View>
          </View>

          {/* Center: Sales Chart */}
          <View style={styles.colCenter}>
            <Text style={styles.sectionTitleSm}>Sales Progression</Text>
            <ProjectSalesChart data={sales_progression ?? []} totalUnits={totalUnits} />
          </View>

          {/* Right: KPI grid 3×3 */}
          <View style={styles.colRight}>
            <Text style={styles.sectionTitleSm}>Key Indicators</Text>
            <View style={styles.kpiGrid}>
              <Kpi
                label="Total Units"
                value={fmtInt(totalUnits)}
                secondary={
                  availableUnits !== null && soldUnits !== null
                    ? `${fmtInt(availableUnits)} avail · ${fmtInt(soldUnits)} sold`
                    : undefined
                }
              />
              <Kpi
                label="Conversion"
                value={fmtPlainPct(conversionRate, 0)}
                secondary="Sold + Resv. vs total"
              />
              <Kpi
                label="Avg Price"
                value={fmtPrice(avgPrice)}
                secondary={fmtRange(minPrice, maxPrice, fmtPrice)}
              />

              <Kpi
                label="Avg €/m²"
                value={fmtSqmPrice(avgSqmPrice)}
                secondary={fmtRange(minSqmPrice, maxSqmPrice, fmtSqmPrice)}
              />
              <Kpi
                label="GPA Range"
                value={fmtRange(minGpa, maxGpa, fmtArea)}
                secondary={`Avg ${fmtArea(avgGpa)}`}
              />
              <Kpi
                label="Avg Time to Sell"
                value={
                  avgMonthsToSell !== null && avgMonthsToSell > 0
                    ? `${fmtDecimal(avgMonthsToSell, 1)} mo`
                    : 'N/A'
                }
                secondary={
                  avgDaysToSell !== null && avgDaysToSell > 0
                    ? `${fmtDecimal(avgDaysToSell, 0)} days`
                    : undefined
                }
              />

              <Kpi
                label="Since Listing"
                value={
                  timeSinceListingMonths === null
                    ? '—'
                    : `${fmtDecimal(timeSinceListingMonths, 0)} mo`
                }
                secondary={
                  project.listing_date ? `Since ${fmtListing(project.listing_date)}` : undefined
                }
              />
              <Kpi
                label="Parking"
                value={fmtPlainPct(parkingPercentage, 0)}
                secondary={
                  unitsWithParking !== null && totalUnits !== null
                    ? `${fmtInt(unitsWithParking)}/${fmtInt(totalUnits)}`
                    : undefined
                }
              />
              <Kpi
                label="Reserved"
                value={fmtInt(reservedUnits)}
                secondary={
                  totalUnits !== null && reservedUnits !== null && totalUnits > 0
                    ? fmtPlainPct((reservedUnits / totalUnits) * 100, 0)
                    : undefined
                }
              />
            </View>
          </View>
        </View>

        {/* ── Typology Breakdown (full width, 7 cols) ────── */}
        <View wrap={false}>
          <Text style={styles.sectionTitleSm}>Typology Breakdown</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeaderRow]}>
              {COL_HEADERS.map((h, i) => (
                <Text
                  key={h}
                  style={[
                    styles.cellHeader,
                    { flex: COL_FLEX[i], textAlign: i === 0 ? 'left' : 'right' },
                  ]}
                >
                  {h}
                </Text>
              ))}
            </View>

            {rows.map((r) => (
              <View key={r.typology} style={styles.tableRow} wrap={false}>
                <Text style={[styles.cell, { flex: COL_FLEX[0], textAlign: 'left' }]}>
                  {r.typology || '—'}
                </Text>
                <Text style={[styles.cell, { flex: COL_FLEX[1], textAlign: 'right' }]}>
                  {fmtInt(r.nr_units)}
                </Text>
                <Text style={[styles.cell, { flex: COL_FLEX[2], textAlign: 'right' }]}>
                  {fmtInt(r.sold_units)}
                </Text>
                <Text style={[styles.cell, { flex: COL_FLEX[3], textAlign: 'right' }]}>
                  {fmtInt(r.reserved_units)}
                </Text>
                <Text style={[styles.cell, { flex: COL_FLEX[4], textAlign: 'right' }]}>
                  {fmtRange(r.min_price_unit, r.max_price_unit, fmtPrice)}
                </Text>
                <Text style={[styles.cell, { flex: COL_FLEX[5], textAlign: 'right' }]}>
                  {fmtSqmPrice(r.avg_price_per_sqm)}
                </Text>
                <Text style={[styles.cell, { flex: COL_FLEX[6], textAlign: 'right' }]}>
                  {fmtRange(r.min_area, r.max_area, fmtArea)}
                </Text>
              </View>
            ))}

            {total && (
              <View style={[styles.tableRow, styles.tableTotalRow]} wrap={false}>
                <Text style={[styles.cellBold, { flex: COL_FLEX[0], textAlign: 'left' }]}>TOTAL</Text>
                <Text style={[styles.cellBold, { flex: COL_FLEX[1], textAlign: 'right' }]}>
                  {fmtInt(total.nr_units)}
                </Text>
                <Text style={[styles.cellBold, { flex: COL_FLEX[2], textAlign: 'right' }]}>
                  {fmtInt(total.sold_units)}
                </Text>
                <Text style={[styles.cellBold, { flex: COL_FLEX[3], textAlign: 'right' }]}>
                  {fmtInt(total.reserved_units)}
                </Text>
                <Text style={[styles.cellBold, { flex: COL_FLEX[4], textAlign: 'right' }]}>
                  {fmtRange(total.min_price_unit, total.max_price_unit, fmtPrice)}
                </Text>
                <Text style={[styles.cellBold, { flex: COL_FLEX[5], textAlign: 'right' }]}>
                  {fmtSqmPrice(total.avg_price_per_sqm)}
                </Text>
                <Text style={[styles.cellBold, { flex: COL_FLEX[6], textAlign: 'right' }]}>
                  {fmtRange(total.min_area, total.max_area, fmtArea)}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* ── Footer ────────────────────────────────────── */}
        <View style={styles.footer}>
          <Text>Generated {fmtDate(generated_at)} · Blue Tagus</Text>
          <Text>{project.website || ''}</Text>
        </View>
      </Page>
    </Document>
  )
}

function AmenityChip({
  label,
  active,
  isLast,
}: {
  label: string
  active: boolean
  isLast?: boolean
}): JSX.Element {
  return (
    <View
      style={[
        styles.amenityChip,
        active && styles.amenityChipActive,
        isLast && styles.amenityChipLast,
      ]}
    >
      <Text style={[styles.amenityChipLabel, active && styles.amenityChipLabelActive]}>
        {label}
      </Text>
      <Text style={[styles.amenityChipStatus, active && styles.amenityChipStatusActive]}>
        {active ? 'Yes' : '—'}
      </Text>
    </View>
  )
}

function Kpi({
  label,
  value,
  secondary,
}: {
  label: string
  value: string
  secondary?: string
}): JSX.Element {
  return (
    <View style={styles.kpiCard}>
      <View style={styles.kpiBox}>
        <Text style={styles.kpiLabel}>{label}</Text>
        <Text style={styles.kpiValue}>{value}</Text>
        {secondary ? <Text style={styles.kpiSecondary}>{secondary}</Text> : null}
      </View>
    </View>
  )
}

function computeStatusBadge(
  status: string | null | undefined,
  soldOutDate: string | null | undefined,
): { label: string; background: string; text: string } {
  const normalized = (status ?? '').trim().toLowerCase()
  if (normalized === 'sold_out' || soldOutDate) {
    return { label: 'Sold Out', background: '#e5e7eb', text: '#374151' }
  }
  if (normalized === 'active') {
    return { label: 'Active', background: '#dcfce7', text: '#16a34a' }
  }
  const label = status
    ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
    : '—'
  return { label, background: '#f3f4f6', text: '#6b7280' }
}

function buildHeadline(
  sold: number | null,
  reserved: number | null,
  total: number | null,
): string {
  const s = sold ?? 0
  const r = reserved ?? 0
  if (!total || total <= 0) {
    return `${s} sold + ${r} reserved`
  }
  const pct = ((s + r) / total) * 100
  return `${pct.toFixed(0)}% sell-through · ${s} sold + ${r} reserved of ${total} units`
}

// "Since 31/03/2026" style for the listing-date secondary line.
function fmtListing(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const dd = String(d.getUTCDate()).padStart(2, '0')
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
    const yyyy = d.getUTCFullYear()
    return `${dd}/${mm}/${yyyy}`
  } catch {
    return iso
  }
}
